<template>
  <div>
    <div v-if="isFetching">
      <v-row>
        <v-col>
          <v-skeleton-loader type="heading" :tile="true" class="d-inline" />
        </v-col>
        <v-col>
          <div class="float-right">
            <v-btn text color="primary" @click="goBack()">
              <v-icon small class="mr-2">fas fa-arrow-left</v-icon>Back
            </v-btn>
          </div>
        </v-col>
      </v-row>
      <div class="mt-10">
        <v-row>
          <v-col>
            <v-skeleton-loader type="image" :tile="true" />
          </v-col>
          <v-col>
            <v-skeleton-loader type="image" :tile="true" />
          </v-col>
          <v-col>
            <v-skeleton-loader type="image" :tile="true" />
          </v-col>
        </v-row>
      </div>
    </div>
    <div v-else>
      <div flat class="pt-2" height="unset" color="grey lighten-2">
        <v-breadcrumbs class="pl-0" :items="breadCrumbsItems" divider=">" />
      </div>
      <div
        flat
        class="pt-2"
        height="unset"
        color="grey lighten-2"
        style="font-family: oswald"
      >
        <div class="w-100">
          <template v-if="editName">
            <v-text-field
              label=""
              v-model="course.name"
              ref="input"
              hide-details
              dense
              @keyup="debounceKeyUp"
              class="d-inline"
            />
            <v-btn x-small @click="toggleClose"> Cancel </v-btn>
          </template>
          <h1 v-else class="font-weight-bold d-inline">
            <span>{{ course.name }} </span>

            <v-btn
              color="primaryMain"
              class="primaryMainText--text"
              @click="toggleEditName"
              x-small
              right
              v-if="canEdit(course.company_id)"
              elevation="0"
            >
              <i class="fal fa-edit mr-2" />
              Edit
            </v-btn>
            <v-tooltip bottom class="w-25">
              <template #activator="{ on }">
                <p v-on="on" class="subtitle-1 w-25">Course</p>
              </template>
              <span>A course is consist of multiple modules</span>
            </v-tooltip>
          </h1>
        </div>
      </div>
      <v-row>
        <v-col cols="12" sm="12" md="12">
          <!-- start of progress bar -->
          <label class="mb-0 mr-0 mr-sm-2 mr-md-3">
            <v-tooltip right>
              <template #activator="{ on }">
                <v-icon x-small v-on="on" class="mr-1">
                  fas fa-info-circle
                </v-icon>
              </template>
              <span>a Course is a set of Modules</span>
            </v-tooltip>
            <b>Course Progress:</b>
          </label>
          <v-progress-linear
            :value="course.completed_percentage"
            color="info"
            height="20"
            striped
            rounded
          >
            <template v-slot="{ value }">
              <strong>{{ Math.ceil(value) }}%</strong>
            </template>
          </v-progress-linear>
          <p class="subtitle-2">
            This progress bar updates whenever you
            <strong>watch or complete</strong> a unit in a module.
          </p>
          <!-- end of progress bar -->
        </v-col>
      </v-row>
      <div class="px-5 mt-10">
        <!-- <v-btn
          v-if="isEdit"
          block
          text
          class="p-5 border-webi mt-5"
          @click="toggleAddModuleDialog"
          >Add Module</v-btn
        > -->

        <v-row>
          <draggable
            :list="course.modules"
            :disabled="!isEdit"
            @change="changeOrder"
            @start="dragging = true"
            @end="dragging = false"
            class="row"
            handle=".handle"
            draggable=".item"
            :options="{ animation: 200, group: 'status' }"
            :element="'div'"
            v-model="course.modules"
          >
            <v-col
              slot="header"
              cols="12"
              sm="12"
              md="6"
              lg="4"
              xl="4"
              class="pointer"
              v-if="canEdit(course.company_id)"
            >
              <article>
                <br />
                <v-img
                  class="white--text px-4 py-4 img-thumbnail"
                  src="https://webinarinc-v2-development.s3-us-west-2.amazonaws.com/categories/4/36168a56b2741b3b52045c4e0ea4165c.jpg"
                  gradient="to top right, rgba(0,0,0,.70), rgba(25,32,72,.7)"
                  cover
                  aspect-ratio="1.778"
                  @click="toggleAddModuleDialog"
                >
                  <v-container fill-height fluid>
                    <v-row align="center" justify="center">
                      <v-col align="center" class="headline">
                        Create Module
                      </v-col>
                    </v-row>
                  </v-container>
                </v-img>
              </article>
            </v-col>
            <template v-for="module in course.modules" v-if="!isFetching">
              <v-col cols="12" sm="12" md="6" lg="4" xl="4" class="handle item">
                <module-card
                  :is-edit="canEdit(course.company_id)"
                  :module="module"
                  v-on:deletedModule="removeDeleted"
                  v-on:module="getModule"
                ></module-card>
              </v-col>
            </template>
          </draggable>
        </v-row>
      </div>
    </div>

    <!-- <v-snackbar :timeout="0" v-model="isEdit">
      You are in Edit Mode
      <v-spacer />
      <v-btn color="error" @click="toggleEditMode" x-small> Close </v-btn>
    </v-snackbar> -->

    <v-snackbar style="margin-bottom: 65px" :timeout="0" v-model="isChanged">
      <v-btn
        class="ma-auto"
        color="success"
        :disabled="isLoading"
        :loading="isLoading"
        @click="saveCourseChanges"
        small
        >Save Changes
      </v-btn>
      <v-btn
        class="mx-auto"
        color="primary"
        :disabled="isLoading"
        :loading="isLoading"
        @click="undoChanges"
        small
        >Undo Changes
      </v-btn>
    </v-snackbar>

    <add-module-dialog
      :category="course"
      :add-module-dialog="addModuleDialog"
      :toggle-add-module-dialog="toggleAddModuleDialog"
      v-on:addedModule="appendModule"
      :closeModal="toggleClose"
    ></add-module-dialog>
    <confirm-dialog
      :close-dialog="toggleConfirmDialog"
      :confirm-action="undoChanges"
      :show-dialog="showConfirmDialog"
      :loading="isLoading"
    >
      Your changes are not yet saved do you want to proceed?
    </confirm-dialog>
    <loading-dialog :loading-dialog="createLoding" :message="createMessage" />
  </div>
</template>
<script>
import ModuleCard from '@/components/course-certification/ModuleCard';
import AddModuleDialog from '@/components/course-certification/includes/AddModuleDialog';
import draggable from 'vuedraggable';
import ConfirmDialog from '@/components/dialogs/ConfirmDialog';
import EventBus from '@/plugins/eventBus';
import LoadingDialog from '@/components/course-certification/includes/LoadingDialog';

import {
  findIndex as _findIndex,
  map as _map,
  isEmpty as _isEmpty,
  some as _some,
  filter as _filter,
  includes as _includes,
  debounce as _debounce,
} from 'lodash-es';

export default {
  name: 'CoursePage',
  components: {
    ModuleCard,
    AddModuleDialog,
    draggable,
    ConfirmDialog,
    LoadingDialog,
  },
  data: () => ({
    createLoding: false,
    createMessage: 'Generating Blank...',
    showConfirmDialog: false,
    isFetching: false,
    isEmpty: _isEmpty,
    course: {
      modules: [],
    },
    isEdit: false,
    addModuleDialog: false,
    isDraggable: false,
    isChanged: false,
    orders: [],
    isLoading: false,
    originalState: null,
    search: '',
    editName: false,
    breadCrumbsItems: [],
  }),

  created() {
    this.fetchCourse();
    EventBus.$on('refreshCourseCertification', () => {
      this.fetchCourse();
    });
    this.debounceFunction = _debounce(this.debounceFunction, 500);
  },
  computed: {
    user() {
      return this.$auth.user();
    },

    isAdmin() {
      return _some(this.$auth.user().roles, { name: 'super-administrator' });
    },

    isManager() {
      return _some(this.$auth.user().roles, { name: 'account-manager' });
    },
  },

  methods: {
    debounceKeyUp(event) {
      this.debounceFunction(event);
    },
    debounceFunction(event) {
      this.changeOrder();
    },
    toggleEditName() {
      this.editName = true;
    },
    generateBreadCrumbs(course) {
      this.breadCrumbsItems = [
        {
          disabled: false,
          to: {
            name: 'Home',
            query: {
              goToSection: 'curriculum_' + course.curriculum_id,
            },
          },
          text: course.curriculum_name + ' - Curriculum',
        },
        {
          disabled: true,
          text: course.name + ' - Course',
        },
      ];
    },
    filteredUnits(modules) {
      if (this.search) {
        return _filter(modules, (module) => {
          const unitExists = _includes(module.units, (unit) => {
            return unit.name
              .toString()
              .toLowerCase()
              .includes(this.search.toLowerCase());
          });

          return unitExists;
        });
      }

      return modules;
    },
    toggleConfirmDialog() {
      this.showConfirmDialog = !this.showConfirmDialog;
    },
    appendModule(item) {
      this.addModuleDialog = false;
      this.course.modules.push(item);
    },
    removeDeleted(item) {
      this.addModuleDialog = false;
      const userIndex = _findIndex(this.course.modules, { id: item.id });
      this.course.modules.splice(this.course.modules.indexOf(userIndex), 1);
      this.isChanged = true;
    },
    getModule(item) {
      const module = item.data;
      if (module.is_draft) {
        const userIndex = _findIndex(this.course.modules, { id: item.id });
        this.course.modules.splice(this.course.modules.indexOf(userIndex), 1);
      }
      this.isChanged = true;
    },
    canEdit(company_id) {
      if (this.isAdmin) {
        this.isEdit = true;
        return true;
      }

      if (this.isManager) {
        this.isEdit = true;
        return this.user.dealer_id == company_id;
      }
    },

    goBack() {
      this.$router.push({
        name: 'Home',
      });
    },
    fetchCourse() {
      this.isFetching = true;
      this.$http
        .get(`/client/lms/course-certifications/${this.$route.params.id}`)
        .then(({ data }) => {
          this.course = data;
          this.originalState = JSON.stringify(data);
          this.$nextTick(() => {
            this.generateBreadCrumbs(data);
            this.isFetching = false;
          });
        })
        .catch(() => {
          this.$router.push({
            name: 'PageNotFound',
          });
        });
    },
    toggleEditMode() {
      this.addModuleDialog = false;
      if (this.isChanged && this.isEdit) {
        this.showConfirmDialog = true;
      } else {
        this.isEdit = !this.isEdit;
      }
    },
    checkMove(e) {
      window.console.log('Future index: ' + e.draggedContext.futureIndex);
      this.isChanged = true;
    },
    changeOrder(e) {
      this.isChanged = true;
      this.orders = {
        id: this.course.id,
        name: this.course.name,
        modules: _map(this.course.modules, 'id'),
      };

      console.log(this.orders);
    },
    formData() {
      const formData = new FormData();
      formData.append('name', 'Blank');
      formData.append('course_id', this.course.id);
      formData.append('order', 0);

      return formData;
    },
    toggleAddModuleDialog() {
      this.createMessage = 'Generating Module...';
      this.createLoding = true;
      this.$store
        .dispatch('modules/saveModule', this.formData())
        .then((response) => {
          this.course.modules.unshift(response.data);
          this.$notify('success', 'Module saved!');
        })
        .catch((error) => {
          this.interactWithErrorBag(error, this.errors);
        })
        // eslint-disable-next-line no-return-assign
        .finally(() => this.close());
    },
    close() {
      this.createMessage = 'Generating Blank...';
      this.createLoding = false;
      this.showConfirmDialog = false;
      this.isLoading = false;
      this.editName = false;
    },
    undoChanges() {
      this.isLoading = true;
      const ogState = JSON.parse(this.originalState);
      this.axios
        .post('/client/lms/courseCertification/updateCourse', {
          data: ogState,
          is_save: false,
        })
        .then(({ data }) => {
          this.course = ogState;
          this.originalState = JSON.stringify(this.course);
        })
        .catch()
        .finally(() => {
          this.isLoading = false;
          this.isChanged = false;
          this.toggleClose();
        });
    },
    saveCourseChanges() {
      this.isLoading = true;
      this.axios
        .post('/client/lms/courseCertification/updateCourse', {
          data: this.course,
          is_save: true,
        })
        .then(({ data }) => {
          console.log(data);
          this.$notify('success', 'Successfully updated!');
        })
        .catch()
        .finally(() => {
          this.isLoading = false;
          this.isChanged = false;
          this.toggleClose();
        });
    },
    toggleClose() {
      this.addModuleDialog = false;
      this.showConfirmDialog = false;
      this.editName = false;
    },
  },
};
</script>

<style scoped>
.edit-btn {
  position: absolute;
  top: 0;
  right: 0;
}
.pointer {
  cursor: pointer;
}
.border-webi {
  border: #f9b418 1px dashed;
}
.pulsing:hover,
.pulsing-active {
  box-shadow: 0 0 0 0 rgba(0, 0, 0, 1);
  animation: pulse-black 1.2s infinite;
}
/* Animation */
@keyframes pulse-black {
  0% {
    box-shadow: 0 0 0 0 rgba(249, 180, 23 0.7);
  }

  70% {
    box-shadow: 0 0 0 10px rgba(249, 180, 23 0);
  }

  100% {
    box-shadow: 0 0 0 0 rgba(249, 180, 23 0);
  }
}
</style>
